import { PortHealthAlert } from '../../../stores/PortHealthStore';

/**
 * Severity levels for port health alerts.
 * Order matters for comparison - higher value = more severe.
 */
export const PortHealthSeverity = {
  None: 0,
  Warning: 1,
  Critical: 2,
} as const;

export type PortHealthSeverity = typeof PortHealthSeverity[keyof typeof PortHealthSeverity];

/**
 * Internal data structure for a processed port health alert.
 */
export interface PortHealthAlertData {
  id: string;
  name: string;
  value: string;
  description: string | null;
  datetime: string | null;
}

/** Standard app bar height in pixels */
export const APP_BAR_HEIGHT = 64;

/** Port health banner height in pixels */
export const BANNER_HEIGHT = 48;

/**
 * Determines the severity level from the value string.
 */
export const getSeverityFromValue = (value: string | null | undefined): PortHealthSeverity => {
  if (!value) return PortHealthSeverity.None;

  const lowerValue = value.toLowerCase();
  if (lowerValue.includes('critical')) return PortHealthSeverity.Critical;
  if (lowerValue.includes('warning')) return PortHealthSeverity.Warning;
  return PortHealthSeverity.None;
};

/**
 * Returns the severity color based on the value content.
 * - Red (#f44336) for Critical
 * - Orange (#ff9800) for Warning and default
 */
export const getSeverityColor = (value: string | null | undefined): string => {
  const severity = getSeverityFromValue(value);
  if (severity === PortHealthSeverity.Critical) return '#f44336';
  return '#ff9800';
};

/**
 * Processes raw port health alerts: maps, filters to visible, and sorts by severity then name.
 */
export const processPortHealthAlerts = (alerts: PortHealthAlert[]): PortHealthAlertData[] => {
  if (!alerts || alerts.length === 0) return [];

  return alerts
    .map((alert) => ({
      id: alert.id,
      name: alert.name,
      value: alert.value,
      description: alert.description ?? null,
      datetime: alert.datetime ?? null,
    }))
    .filter((alert) => getSeverityFromValue(alert.value) !== PortHealthSeverity.None)
    .sort((a, b) => {
      const severityA = getSeverityFromValue(a.value);
      const severityB = getSeverityFromValue(b.value);
      if (severityB !== severityA) return severityB - severityA;
      return a.name.localeCompare(b.name);
    });
};

/** Known type keywords for alert name parsing */
const TYPE_KEYWORDS = ['Wind', 'Tide', 'Wave'];

/**
 * Parses alert name like "PortOfBrisbane-EntranceChannelWindStatus"
 * into { location: "Entrance Channel", type: "Wind" }
 *
 * Supports two formats:
 * - 3-part: PortOf{Port}-{Location}-{Type}Status
 * - 2-part: PortOf{Port}-{Location}{Type}Status (location+type combined)
 */
export const parseAlertName = (name: string): { location: string; type: string } => {
  const parts = name.split('-');

  if (parts.length >= 3) {
    const location = parts[1].replace(/([A-Z])/g, ' $1').trim();
    const type = parts[2].replace('Status', '').replace(/([A-Z])/g, ' $1').trim();
    return { location, type };
  }

  if (parts.length === 2) {
    const combined = parts[1].replace('Status', '');

    for (const keyword of TYPE_KEYWORDS) {
      if (combined.endsWith(keyword)) {
        const locationPart = combined.slice(0, -keyword.length);
        const location = locationPart.replace(/([A-Z])/g, ' $1').trim();
        return { location, type: keyword };
      }
    }

    const location = combined.replace(/([A-Z])/g, ' $1').trim();
    return { location, type: '' };
  }

  return { location: name, type: '' };
};

/** Chip style constants for inline styles (MUI v4 compat) */
export const CHIP_LOCATION_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.2)',
  color: 'white',
  height: 24,
};

export const CHIP_TYPE_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.3)',
  color: 'white',
  height: 24,
};

export const CHIPS_CONTAINER_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  alignItems: 'center',
};
